import { ActionIcon, Badge, Button, Checkbox, Combobox, Group, Indicator, ScrollArea, Text, Tooltip, useCombobox } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSearch, IconTag } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSaveTag, useTags } from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  variant?: 'icon' | 'field';
  disabled?: boolean;
}

/** Mehrfachauswahl von Tags mit Suche und „Tag anlegen“. */
export function TagPicker({ value, onChange, required, variant = 'icon', disabled }: Props) {
  const { t } = useTranslation();
  const { workspaceId, workspace, isAdmin } = useCurrentWorkspace();
  const { data: tags = [] } = useTags(workspaceId);
  const saveTag = useSaveTag(workspaceId);
  const [search, setSearch] = useState('');
  const combobox = useCombobox({ onDropdownClose: () => setSearch('') });
  const active = tags.filter((tag) => !tag.archived || value.includes(tag.id));
  const filtered = active.filter((tag) => tag.name.toLowerCase().includes(search.trim().toLowerCase()));
  const selectedTags = tags.filter((tag) => value.includes(tag.id));
  const canCreate = workspace.settings.permissions.createTags === 'EVERYONE' || isAdmin;

  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const create = async () => {
    try {
      const tag = await saveTag.mutateAsync({ name: search.trim() });
      onChange([...value, tag.id]);
      setSearch('');
    } catch (e) {
      notifications.show({ color: 'red', message: (e as Error).message });
    }
  };

  const target =
    variant === 'icon' ? (
      selectedTags.length > 0 ? (
        <Group gap={4} style={{ cursor: disabled ? undefined : 'pointer' }} onClick={() => !disabled && combobox.toggleDropdown()}>
          {selectedTags.map((tag) => (
            <Badge key={tag.id} variant="light" color="gray" radius="sm" tt="none">
              {tag.name}
            </Badge>
          ))}
        </Group>
      ) : (
        <Tooltip label={t('picker.tags')}>
          <Indicator disabled={!required} color="red" size={6} offset={4}>
            <ActionIcon variant="subtle" color="gray" onClick={() => combobox.toggleDropdown()} disabled={disabled} aria-label={t('picker.tags')}>
              <IconTag size={18} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      )
    ) : (
      <Button variant="default" fullWidth justify="flex-start" onClick={() => combobox.toggleDropdown()} disabled={disabled}>
        {selectedTags.length ? selectedTags.map((tag) => tag.name).join(', ') : <Text c="dimmed" size="sm">{t('picker.tags')}</Text>}
      </Button>
    );

  return (
    <Combobox store={combobox} width={260} position="bottom-end" withinPortal>
      <Combobox.Target>{target}</Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder={t('picker.searchTags')}
          leftSection={<IconSearch size={14} />}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={260} type="scroll">
            {filtered.map((tag) => (
              <Combobox.Option key={tag.id} value={tag.id} onClick={() => toggle(tag.id)}>
                <Checkbox label={tag.name} checked={value.includes(tag.id)} readOnly size="xs" />
              </Combobox.Option>
            ))}
            {filtered.length === 0 && <Combobox.Empty>{t('picker.noTags')}</Combobox.Empty>}
          </ScrollArea.Autosize>
        </Combobox.Options>
        {canCreate && search.trim() && !tags.some((tag) => tag.name.toLowerCase() === search.trim().toLowerCase()) && (
          <Combobox.Footer>
            <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={() => void create()} loading={saveTag.isPending}>
              {t('picker.createTag', { name: search.trim() })}
            </Button>
          </Combobox.Footer>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
}
